/**
 * HighlightedTitle component for displaying text with emphasized sections.
 * Used to create eye-catching titles in advertisement banners with part of the text bolded.
 */
import React from "react";
import RText from "../../RText";
import variables from "../../../styles/variables";
import { Platform } from "react-native";
import { useResponsive } from "../../../hooks/useResponsive";

/**
 * Props for the HighlightedTitle component.
 * @typedef {Object} HighlightedTitleProps
 * @property {string} title - The complete title text to display.
 * @property {string} [emphasisedTitle] - The part of the title to emphasize with bold styling.
 * @property {string} [accentColor] - The color to use for the text (defaults to theme primary color).
 * @property {boolean} [fullScreen] - Whether the title is displayed in a full-screen context, affecting font size.
 */
type HighlightedTitleProps = {
  title: string;
  emphasisedTitle?: string;
  accentColor?: string;
  fullScreen?: boolean;
};

/**
 * A component that renders a title with an emphasized (bold) section.
 * Adjusts font size and styling based on screen size and platform.
 * If no emphasized text is provided or found within the title, renders the entire title normally.
 *
 * @param {HighlightedTitleProps} props - Component props
 * @returns {JSX.Element} The rendered title with optional emphasized section
 */
const HighlightedTitle = ({
  title,
  emphasisedTitle,
  accentColor,
  fullScreen,
}: HighlightedTitleProps) => {
  const { isSmallAndUp, isXSmallAndUp, isLargeAndUp } = useResponsive();

  const isWeb = Platform.OS === "web";

  /**
   * Determine appropriate font size based on screen size and display mode
   */
  let fontSize = "xl";
  if (isXSmallAndUp) fontSize = "xxl";
  if (isLargeAndUp) fontSize = "xxxl";
  if (fullScreen && isWeb && isSmallAndUp) fontSize = "supersize";

  /**
   * Adjust line height for better readability on larger displays
   */
  let lineHeight = 36;
  if (fullScreen && isSmallAndUp) lineHeight = 80;

  // If there is no emphasisedTitle or it is not found in the title
  if (!emphasisedTitle || !title.includes(emphasisedTitle)) {
    return (
      <RText
        size={fontSize}
        uppercase
        color={accentColor}
        style={{ textAlign: "center" }}
        weight="semiBold"
      >
        {title}
      </RText>
    );
  }

  /**
   * Split the title by the emphasized part to render normal and bold sections separately
   */
  const parts = title.split(emphasisedTitle);

  return (
    <RText
      size={fontSize}
      style={[
        {
          textAlign: "center",
        },
        isWeb && { lineHeight: lineHeight },
      ]}
      color={accentColor}
    >
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && (
            <RText weight="bold" size={fontSize} color={accentColor} uppercase>
              {emphasisedTitle}
            </RText>
          )}
        </React.Fragment>
      ))}
    </RText>
  );
};

export default HighlightedTitle;
